import React, { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import clsx from 'clsx';
import { Modal } from './ui/Modal';
import { useCreateFolder } from '../services/folders';
import { useCurrentUser } from '../services/userHooks';
import { useToast } from '../hooks/useToast';

interface AddFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AddFolderModal({ isOpen, onClose }: AddFolderModalProps) {
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useCurrentUser();
  const createFolderMutation = useCreateFolder();
  const { showToast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setError(null);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentUser) {
      setError(t`User not logged in`);
      return;
    }

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(t`Folder title is required`);
      return;
    }

    if (trimmedTitle.length > 250) {
      setError(t`Folder title must be 250 characters or less`);
      return;
    }

    try {
      await createFolderMutation.mutateAsync({
        title: trimmedTitle,
        formKey: currentUser.formKey,
      });
      showToast(t`${trimmedTitle} folder created`, 'success');
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t`Sorry, there was an error. Please try again.`;
      setError(errorMessage);
    }
  };

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={t`Add folder`}
      initialFocusRef={inputRef}
      size="xl"
    >
      <Modal.Header title={t`Add folder`} />
      <Modal.Body>
        <form onSubmit={handleSubmit} className="flex flex-row gap-4">
          <input
            ref={inputRef}
            type="text"
            name="title"
            aria-label={t`Folder title`}
            value={title}
            onChange={handleInputChange}
            placeholder={t`Folder title`}
            className={clsx('form-input', error && 'form-input-error')}
            maxLength={250}
            disabled={createFolderMutation.isPending}
          />

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={createFolderMutation.isPending}
            className={clsx(
              'button-primary px-12 py-4',
              createFolderMutation.isPending && 'bg-surface-secondary',
            )}
          >
            {createFolderMutation.isPending ? (
              <Trans>Adding...</Trans>
            ) : (
              <Trans>Add</Trans>
            )}
          </button>
        </form>
      </Modal.Body>
    </Modal.Root>
  );
}

export default AddFolderModal;
